import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { MasterSolutionDetailTableModel } from 'src/app/models/tables/preliminaries/master-solution-detail-table.model';
import { MasterSolutionTableModel } from 'src/app/models/tables/preliminaries/master-solution-table.model';
import { HeaderService } from 'src/app/services/header/header.service';
import { MasterSolutionDetailService } from 'src/app/services/preliminaries/master-solution-detail.service';
import { MasterSolutionService } from 'src/app/services/preliminaries/master-solution.service';

@Component({
  selector: 'app-master-solution',
  templateUrl: './master-solution.component.html',
  styleUrls: ['./master-solution.component.scss'],
  standalone: false
})
export class MasterSolutionComponent implements OnInit {

  activeTabIndex = 0;
  ascendant: boolean = true;
  columnSort: string | null = null;
  detailData: PagedResponseModel<MasterSolutionDetailTableModel> = new PagedResponseModel<MasterSolutionDetailTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  filters: ColumnResponseModel[] = []
  generalData: PagedResponseModel<MasterSolutionTableModel> = new PagedResponseModel<MasterSolutionTableModel>();
  generalHeader: ColumnResponseModel[] = [];
  isOpened: string | undefined = undefined
  limit: number = 10;
  nameWindow: string = 'Maestra Soluciones';
  page: number = 1;
  previousSort: { field: string; isAscsendant: number };
  projectName = ProjectName.projectName;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    }
  ];
  url: string = 'master-solution';

  constructor(private masterSolutionService: MasterSolutionService,
    private masterSolutionDetailService: MasterSolutionDetailService,
    private router: Router,
    private headerService: HeaderService,
    private title: Title) { }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem(`${this.url}`);
    if (filters) this.filters = JSON.parse(filters);
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getAllMasterSolutionsWithPagination(),
    ]);
  }

  async exportDetailConsultation() {
    try {
      const blob = await firstValueFrom(this.masterSolutionDetailService.exportDetailConsultation(this.filters, this.columnSort, this.ascendant));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Solución Detalle.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar los detalles de la solución.', error);
    }
  }

  async exportGeneralConsultation() {
    try {
      const blob = await firstValueFrom(this.masterSolutionService.exportGeneralConsultation(this.filters, this.columnSort, this.ascendant));
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Soluciones.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al exportar las soluciones.', error);
    }
  }

  async getColumnsForDetailConsultation() {
    try {
      if (this.detailHeader.length === 0) {
        this.detailHeader = await firstValueFrom(this.masterSolutionDetailService.getColumnsForDetailConsultation());
      }
    } catch (error) {
      console.error('Error al obtener las columnas para la consulta de detalles.', error);
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        this.generalHeader = await firstValueFrom(this.masterSolutionService.getColumnsForGeneralConsultation());
      }
    } catch (error) {
      console.error('Error al obtener las columnas para la consulta general.', error);
    }
  }

  async getAllMasterSolutionsWithPagination() {
    try {
      this.generalData = await firstValueFrom(this.masterSolutionService.getAllMasterSolutionsWithPagination(this.page,
        this.limit, this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error al obtener la solución.', error);
    }
  }

  async getAllMasterSolutionDetailsWithPagination() {
    try {
      this.detailData = await firstValueFrom(this.masterSolutionDetailService.getAllMasterSolutionDetailsWithPagination(this.page,
        this.limit, this.columnSort, this.ascendant, this.filters));
    } catch (error) {
      console.error('Error al obtener los detalles de la solución.', error);
    }
  }

  public async handleTabChange(tabIndex: number) {
    this.generalHeader.length = this.generalHeader.length === 1 ? 0 : this.generalHeader.length;
    this.detailHeader.length = this.detailHeader.length === 1 ? 0 : this.detailHeader.length;
    if (this.activeTabIndex === tabIndex) return;
    this.page = 1;
    this.limit = 10;
    this.activeTabIndex = tabIndex;
    switch (this.activeTabIndex) {
      case 0:
        await Promise.all([
          this.getColumnsForGeneralConsultation(),
          this.getAllMasterSolutionsWithPagination()
        ]);
        this.generalData = {
          ...this.generalData,
          currentPage: 1,
          pageSize: 10
        };
        break;
      case 1:
        await Promise.all([
          this.getColumnsForDetailConsultation(),
          this.getAllMasterSolutionDetailsWithPagination()
        ]);
        this.detailData = {
          ...this.detailData,
          currentPage: 1,
          pageSize: 10
        };
        break;
    }
  }

  newRegister() {
    this.router.navigate([`/${this.url}/register`]);
  }

  onAction(event: { action: string, item?: MasterSolutionTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit' && event.item) {
      this.router.navigate([`/${this.url}/register/${event.item.id}`]);
    }
  }

  async onExport() {
    switch (this.activeTabIndex) {
      case 0:
        await this.exportGeneralConsultation();
        break;
      case 1:
        await this.exportDetailConsultation();
        break;
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);

    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    switch (this.activeTabIndex) {
      case 0:
        await this.getAllMasterSolutionsWithPagination()
        break;
      case 1:
        await this.getAllMasterSolutionDetailsWithPagination()
        break;
    }
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }
}
